import { COMPOSITE_KEYS, ENTITY_TABLES } from './constants';
import type { OfflineStorage } from './storage';
import type { CachedEntity, EntityType, RouteMatch } from './types';
import { nowIso, serverTimestamp } from './timestamps';

function entityIdFromDeleteUrl(url: string): string | undefined {
  const path = url.split('?')[0] ?? url;
  const segments = path.split('/').filter(Boolean);
  const last = segments.at(-1);
  if (!last || last === 'use-session' || last === 'restore-session' || last === 'redeem' || last === 'complete' || last === 'convert') {
    return segments.at(-2);
  }
  return last;
}

export function createCacheOps(storage: OfflineStorage) {
  async function pruneRemappedTempEntities(entity: EntityType): Promise<void> {
    if (!storage.isEntityTable(entity)) return;

    const mappings = await storage.listIdMappingsByEntity(entity);
    if (mappings.length === 0) return;

    await Promise.all(mappings.map((mapping) => storage.deleteEntityRecord(entity, mapping.tempId)));
  }

  async function getPendingDeleteIds(entityType: EntityType): Promise<Set<string>> {
    const queue = await storage.listQueueItems();
    const ids = new Set<string>();

    for (const item of queue) {
      if (item.method !== 'DELETE' || item.entityType !== entityType) continue;
      if (item.entityId) ids.add(item.entityId);
      const idFromUrl = entityIdFromDeleteUrl(item.url);
      if (idFromUrl) ids.add(idFromUrl);
    }

    return ids;
  }

  function toCached(record: Record<string, unknown>, pendingSync = false): CachedEntity {
    const localUpdatedAt =
      typeof record._localUpdatedAt === 'string'
        ? record._localUpdatedAt
        : serverTimestamp(record);
    return {
      ...record,
      id: String(record.id),
      _localUpdatedAt: localUpdatedAt,
      _pendingSync: pendingSync,
    };
  }

  async function upsertEntity(
    entity: EntityType,
    record: Record<string, unknown>,
    pendingSync = false,
    options?: { force?: boolean },
  ): Promise<void> {
    if (!storage.isEntityTable(entity)) return;

    const id = String(record.id);
    if (!pendingSync && !options?.force) {
      const existing = await storage.getEntityRecord(entity, id);
      if (existing?._pendingSync) return;
      const pendingDeletes = await getPendingDeleteIds(entity);
      if (pendingDeletes.has(id)) return;
    }

    await storage.putEntityRecord(entity, toCached(record, pendingSync));
  }

  async function upsertMany(
    entity: EntityType,
    records: Record<string, unknown>[],
    pendingSync = false,
    options?: { force?: boolean },
  ): Promise<void> {
    if (!storage.isEntityTable(entity) || records.length === 0) return;

    const pendingDeletes = options?.force ? new Set<string>() : await getPendingDeleteIds(entity);
    const toWrite: CachedEntity[] = [];

    for (const record of records) {
      const id = String(record.id);
      if (pendingDeletes.has(id)) continue;

      if (!pendingSync && !options?.force) {
        const existing = await storage.getEntityRecord(entity, id);
        if (existing?._pendingSync) continue;
      }

      toWrite.push(toCached(record, pendingSync));
    }

    if (toWrite.length > 0) {
      await storage.bulkPutEntityRecords(entity, toWrite);
    }
  }

  async function deleteEntity(entity: EntityType, id: string): Promise<void> {
    if (!storage.isEntityTable(entity)) return;
    await storage.deleteEntityRecord(entity, id);
  }

  async function getEntity(entity: EntityType, id: string): Promise<CachedEntity | undefined> {
    if (!storage.isEntityTable(entity)) return undefined;
    return storage.getEntityRecord(entity, id);
  }

  async function listEntities(entity: EntityType): Promise<CachedEntity[]> {
    if (!storage.isEntityTable(entity)) return [];
    return storage.listEntityRecords(entity);
  }

  async function setComposite(
    key: string,
    data: unknown,
    pendingSync = false,
    options?: { force?: boolean },
  ): Promise<void> {
    if (!pendingSync && !options?.force) {
      const existing = await storage.getCompositeEntry(key);
      const existingData = existing?.data as Record<string, unknown> | undefined;
      if (existingData?._pendingSync) return;
    }

    const payload =
      pendingSync && typeof data === 'object' && data !== null
        ? { ...(data as Record<string, unknown>), _pendingSync: true }
        : data;

    await storage.putCompositeEntry({ key, data: payload, _localUpdatedAt: nowIso() });
  }

  async function getComposite(key: string): Promise<unknown | undefined> {
    const entry = await storage.getCompositeEntry(key);
    return entry?.data;
  }

  async function cacheResponse(
    route: RouteMatch,
    data: unknown,
    url: string,
    params?: Record<string, unknown>,
    options?: { force?: boolean; requestData?: Record<string, unknown> },
  ): Promise<void> {
    if (route.operation === 'delete') {
      const id =
        route.id ??
        (typeof data === 'object' && data !== null && 'id' in data
          ? String((data as { id: unknown }).id)
          : undefined);
      if (id && storage.isEntityTable(route.entity)) {
        await deleteEntity(route.entity, id);
        if (route.entity === 'trainingPlans') {
          await storage.deleteCompositeEntry(COMPOSITE_KEYS.trainingPlanDetail(id));
        }
      }
      return;
    }

    if (data === undefined || data === null) return;

    switch (route.entity) {
      case 'clients':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('clients', data as Record<string, unknown>[], false, options);
        } else if (
          (route.operation === 'get' || route.operation === 'update') &&
          typeof data === 'object'
        ) {
          await upsertEntity('clients', data as Record<string, unknown>, false, options);
        }
        break;

      case 'bookings':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('bookings', data as Record<string, unknown>[], false, options);
        } else if (typeof data === 'object') {
          await upsertEntity('bookings', data as Record<string, unknown>, false, options);
        }
        break;

      case 'tasks':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('tasks', data as Record<string, unknown>[], false, options);
        } else if (typeof data === 'object') {
          await upsertEntity('tasks', data as Record<string, unknown>, false, options);
        }
        break;

      case 'leads':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('leads', data as Record<string, unknown>[], false, options);
        } else if (typeof data === 'object') {
          await upsertEntity('leads', data as Record<string, unknown>, false, options);
        }
        break;

      case 'measurements':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('measurements', data as Record<string, unknown>[], false, options);
        } else if (typeof data === 'object') {
          await upsertEntity('measurements', data as Record<string, unknown>, false, options);
        }
        break;

      case 'packages':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('packages', data as Record<string, unknown>[], false, options);
        } else if (typeof data === 'object') {
          await upsertEntity('packages', data as Record<string, unknown>, false, options);
        }
        break;

      case 'challenges':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('challenges', data as Record<string, unknown>[], false, options);
        } else if (typeof data === 'object') {
          await upsertEntity('challenges', data as Record<string, unknown>, false, options);
        }
        break;

      case 'trainingPlans':
        if (route.operation === 'list' && Array.isArray(data)) {
          await upsertMany('trainingPlans', data as Record<string, unknown>[], false, options);
        } else if (route.operation === 'get' && typeof data === 'object') {
          const plan = data as Record<string, unknown>;
          const planId = route.id ?? String(plan.id);
          if (!options?.force && planId) {
            const existing = await getEntity('trainingPlans', planId);
            if (existing?._pendingSync) break;
          }
          await upsertEntity('trainingPlans', plan, false, options);
          if (route.id) {
            await setComposite(COMPOSITE_KEYS.trainingPlanDetail(route.id), data, false, options);
          }
        } else if (route.operation === 'create' && typeof data === 'object') {
          const plan = data as Record<string, unknown>;
          const planId = String(plan.id);
          await upsertEntity('trainingPlans', plan, false, options);
          if (planId && options?.requestData?.exercises !== undefined) {
            await setComposite(
              COMPOSITE_KEYS.trainingPlanDetail(planId),
              { ...plan, exercises: options.requestData.exercises },
              false,
              options,
            );
          }
        } else if (route.operation === 'update' && typeof data === 'object') {
          const plan = data as Record<string, unknown>;
          const planId = route.id ?? String(plan.id);
          await upsertEntity('trainingPlans', plan, false, options);
          if (planId) {
            const exercises = Array.isArray(plan.exercises)
              ? plan.exercises
              : options?.requestData?.exercises !== undefined
                ? options.requestData.exercises
                : undefined;
            if (exercises !== undefined) {
              await setComposite(
                COMPOSITE_KEYS.trainingPlanDetail(planId),
                { ...plan, exercises },
                false,
                options,
              );
            }
          }
        } else if (typeof data === 'object') {
          await upsertEntity('trainingPlans', data as Record<string, unknown>, false, options);
        }
        break;

      case 'trainerProfile':
        if (typeof data === 'object') {
          await setComposite(COMPOSITE_KEYS.trainerProfile, data, false, options);
        }
        break;

      case 'clientCoins':
        if (route.clientId && typeof data === 'object') {
          await setComposite(COMPOSITE_KEYS.clientCoins(route.clientId), data, false, options);
        }
        break;

      case 'analyticsRetention':
        await setComposite(COMPOSITE_KEYS.analyticsRetention, data, false, options);
        break;

      case 'analyticsFunnel':
        await setComposite(COMPOSITE_KEYS.analyticsFunnel, data, false, options);
        break;
    }

    void url;
    void params;
  }

  function stripMeta(entity: CachedEntity): Record<string, unknown> {
    const { _localUpdatedAt, _pendingSync, ...rest } = entity;
    return rest;
  }

  async function readCached(
    route: RouteMatch,
    url: string,
    params?: Record<string, unknown>,
  ): Promise<unknown> {
    if (route.operation === 'list' && storage.isEntityTable(route.entity)) {
      await pruneRemappedTempEntities(route.entity);
    }

    switch (route.entity) {
      case 'clients':
        if (route.operation === 'list') {
          return (await listEntities('clients')).map(stripMeta);
        }
        if (route.id) {
          const client = await getEntity('clients', route.id);
          if (!client) throw new Error('offline_cache_miss');
          return stripMeta(client);
        }
        break;

      case 'bookings':
        if (route.operation === 'list') {
          const all = await listEntities('bookings');
          return all.map(stripMeta);
        }
        if (route.id) {
          const booking = await getEntity('bookings', route.id);
          if (!booking) throw new Error('offline_cache_miss');
          return stripMeta(booking);
        }
        break;

      case 'tasks': {
        let tasks = await listEntities('tasks');
        if (params?.status) {
          tasks = tasks.filter((t) => t.status === params.status);
        }
        if (params?.clientId) {
          tasks = tasks.filter((t) => t.clientId === params.clientId);
        }
        if (route.operation === 'list') {
          return tasks.map(stripMeta);
        }
        if (route.id) {
          const task = tasks.find((t) => t.id === route.id);
          if (!task) throw new Error('offline_cache_miss');
          return stripMeta(task);
        }
        break;
      }

      case 'leads':
        if (route.operation === 'list') {
          return (await listEntities('leads')).map(stripMeta);
        }
        if (route.id) {
          const lead = await getEntity('leads', route.id);
          if (!lead) throw new Error('offline_cache_miss');
          return stripMeta(lead);
        }
        break;

      case 'measurements':
        if (route.operation === 'list' && route.clientId) {
          const all = await listEntities('measurements');
          return all.filter((m) => m.clientId === route.clientId).map(stripMeta);
        }
        if (route.id) {
          const m = await getEntity('measurements', route.id);
          if (!m) throw new Error('offline_cache_miss');
          return stripMeta(m);
        }
        break;

      case 'packages':
        if (route.operation === 'list' && route.clientId) {
          const all = await listEntities('packages');
          return all.filter((p) => p.clientId === route.clientId).map(stripMeta);
        }
        if (route.id) {
          const pkg = await getEntity('packages', route.id);
          if (!pkg) throw new Error('offline_cache_miss');
          return stripMeta(pkg);
        }
        break;

      case 'challenges':
        if (route.operation === 'list') {
          const all = await listEntities('challenges');
          const clientId = params?.clientId as string | undefined;
          const filtered = clientId ? all.filter((c) => c.clientId === clientId) : all;
          return filtered.map(stripMeta);
        }
        if (route.id) {
          const ch = await getEntity('challenges', route.id);
          if (!ch) throw new Error('offline_cache_miss');
          return stripMeta(ch);
        }
        break;

      case 'trainingPlans':
        if (route.operation === 'list') {
          return (await listEntities('trainingPlans')).map(stripMeta);
        }
        if (route.id) {
          const detail = await getComposite(COMPOSITE_KEYS.trainingPlanDetail(route.id));
          if (detail) {
            const record = detail as Record<string, unknown>;
            if (!Array.isArray(record.exercises)) {
              throw new Error('offline_cache_miss');
            }
            return detail;
          }
          throw new Error('offline_cache_miss');
        }
        break;

      case 'trainerProfile': {
        const profile = await getComposite(COMPOSITE_KEYS.trainerProfile);
        if (!profile) throw new Error('offline_cache_miss');
        return profile;
      }

      case 'clientCoins':
        if (route.clientId) {
          const coins = await getComposite(COMPOSITE_KEYS.clientCoins(route.clientId));
          if (!coins) throw new Error('offline_cache_miss');
          return coins;
        }
        break;

      case 'analyticsRetention': {
        const data = await getComposite(COMPOSITE_KEYS.analyticsRetention);
        if (!data) throw new Error('offline_cache_miss');
        return data;
      }

      case 'analyticsFunnel': {
        const data = await getComposite(COMPOSITE_KEYS.analyticsFunnel);
        if (!data) throw new Error('offline_cache_miss');
        return data;
      }
    }

    void url;
    throw new Error('offline_cache_miss');
  }

  async function remapEntityId(entity: EntityType, tempId: string, realId: string): Promise<void> {
    if (!ENTITY_TABLES.includes(entity)) return;
    if (!storage.isEntityTable(entity)) return;

    const existing = await storage.getEntityRecord(entity, tempId);
    if (existing) {
      const { _localUpdatedAt, _pendingSync, ...rest } = existing;
      await storage.deleteEntityRecord(entity, tempId);
      await storage.putEntityRecord(entity, {
        ...rest,
        id: realId,
        _localUpdatedAt: nowIso(),
        _pendingSync: false,
      });
    }

    const all = await storage.listEntityRecords(entity);
    for (const record of all) {
      let changed = false;
      const updated = { ...record };

      if (updated.clientId === tempId) {
        updated.clientId = realId;
        changed = true;
      }
      if (updated.trainerId === tempId) {
        updated.trainerId = realId;
        changed = true;
      }

      if (changed) {
        await storage.putEntityRecord(entity, updated);
      }
    }

    if (entity === 'trainingPlans') {
      const oldKey = COMPOSITE_KEYS.trainingPlanDetail(tempId);
      const detail = await getComposite(oldKey);
      if (detail) {
        await storage.deleteCompositeEntry(oldKey);
        const record = detail as Record<string, unknown>;
        await setComposite(
          COMPOSITE_KEYS.trainingPlanDetail(realId),
          { ...record, id: realId },
          false,
          { force: true },
        );
      }
    }
  }

  async function markSynced(entity: EntityType, id: string): Promise<void> {
    if (!storage.isEntityTable(entity)) return;
    const existing = await storage.getEntityRecord(entity, id);
    if (existing) {
      await storage.putEntityRecord(entity, {
        ...existing,
        _pendingSync: false,
        _localUpdatedAt: nowIso(),
      });
    }
  }

  async function setLastSyncAt(iso: string): Promise<void> {
    await storage.setMeta('lastSyncAt', iso);
  }

  async function getLastSyncAt(): Promise<string | null> {
    return storage.getMeta('lastSyncAt');
  }

  return {
    toCached,
    upsertEntity,
    upsertMany,
    deleteEntity,
    getEntity,
    listEntities,
    setComposite,
    getComposite,
    cacheResponse,
    readCached,
    remapEntityId,
    markSynced,
    setLastSyncAt,
    getLastSyncAt,
  };
}

export type CacheOps = ReturnType<typeof createCacheOps>;
